// storage.js - R2 업로드
// NOTE: R2는 브라우저에서 직접 인증 불가능.
// QR 코드의 endpoint는 Cloudflare Worker URL이어야 함.
// Worker가 인증을 처리하고 R2에 업로드함.

import { getPairingInfo } from './pairing.js';
import { WEB_VERSION } from './main.js';
import { withSpan, getSentryTraceHeaders } from './sentry.js';
import { getWebSocket } from './lib/session-health.js';

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;
const WS_CHUNK_SIZE = 100 * 1024; // 100KB

/**
 * R2에 파일 업로드 (Worker 경유)
 * @param {Blob} blob - 업로드할 파일
 * @param {string} fileName - 파일명
 * @param {Function} onProgress - 진행률 콜백 (0-100)
 * @returns {Promise<Object>} 업로드 결과
 */
export async function uploadToR2(blob, fileName, onProgress = () => {}) {
  const pairingInfo = getPairingInfo();

  if (!pairingInfo) {
    throw new Error('페어링 정보가 없습니다. 다시 페어링해주세요.');
  }

  const { endpoint, credentials, device_id } = pairingInfo;

  let lastError = null;

  for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
    try {
      onProgress(attempt === 1 ? 10 : 10 + (attempt - 1) * 5);

      const result = await withSpan(
        'http.upload',
        `Upload ${fileName}`,
        () => uploadWithXHR(
          blob,
          fileName,
          endpoint,
          credentials,
          device_id,
          (progress) => {
            // 진행률: 10-90% 구간
            onProgress(10 + (progress * 80));
          }
        ),
        { 'file.name': fileName, 'file.size': blob.size, attempt }
      );

      onProgress(100);
      return result;

    } catch (e) {
      console.error(`Upload attempt ${attempt} failed:`, e);
      lastError = e;

      // 세션 에러는 재시도해도 의미 없음
      if (e.requiresRepairing) {
        throw e;
      }

      if (attempt < MAX_RETRIES) {
        await sleep(RETRY_DELAY * attempt);
      }
    }
  }

  throw new Error(`업로드 실패 (${MAX_RETRIES}회 시도): ${lastError?.message || '알 수 없는 오류'}`);
}

/**
 * XHR을 사용한 업로드 (진행률 지원)
 * Worker endpoint로 업로드하며, Worker가 R2에 저장
 */
async function uploadWithXHR(blob, fileName, endpoint, credentials, deviceId, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    // Worker endpoint에 파일명 포함
    const url = `${endpoint}/upload/${fileName}`;

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) {
        onProgress(e.loaded / e.total);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        let response;
        try {
          response = JSON.parse(xhr.responseText);
        } catch {
          response = { success: true };
        }
        resolve({
          success: true,
          fileName: fileName,
          pcOnline: response.pcOnline,
          warning: response.warning,
          ...response
        });
      } else {
        let errorMsg = xhr.statusText;
        try {
          const errResponse = JSON.parse(xhr.responseText);
          errorMsg = errResponse.error || errorMsg;
        } catch {}

        // Handle specific error codes that require re-pairing
        if (xhr.status === 401) {
          const error = new Error('세션이 유효하지 않습니다. 다시 페어링해주세요.');
          error.requiresRepairing = true;
          reject(error);
        } else if (xhr.status === 404) {
          const error = new Error('세션을 찾을 수 없습니다. 다시 페어링해주세요.');
          error.requiresRepairing = true;
          reject(error);
        } else if (xhr.status === 410) {
          const error = new Error('세션이 만료되었습니다. 다시 페어링해주세요.');
          error.requiresRepairing = true;
          reject(error);
        } else {
          reject(new Error(`HTTP ${xhr.status}: ${errorMsg}`));
        }
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('네트워크 오류'));
    });

    xhr.addEventListener('timeout', () => {
      reject(new Error('업로드 시간 초과'));
    });

    xhr.open('PUT', url);
    xhr.timeout = 60000; // 60초 타임아웃

    // 헤더 설정
    xhr.setRequestHeader('Content-Type', 'application/pdf');
    xhr.setRequestHeader('X-Device-ID', deviceId);
    xhr.setRequestHeader('X-Timestamp', new Date().toISOString());
    xhr.setRequestHeader('X-Web-Version', WEB_VERSION);

    // API 키 인증 (Worker에서 검증)
    if (credentials.apiKey) {
      xhr.setRequestHeader('Authorization', `Bearer ${credentials.apiKey}`);
    }

    // Sentry trace headers for distributed tracing
    const traceHeaders = getSentryTraceHeaders();
    if (traceHeaders['sentry-trace']) {
      xhr.setRequestHeader('sentry-trace', traceHeaders['sentry-trace']);
    }
    if (traceHeaders.baggage) {
      xhr.setRequestHeader('baggage', traceHeaders.baggage);
    }

    xhr.send(blob);
  });
}

/**
 * WebSocket을 통한 직접 파일 전송 (R2 우회)
 * Web Desktop이 연결된 경우 DO를 통해 binary relay
 * @param {Blob} blob - 업로드할 파일
 * @param {string} fileName - 파일명
 * @param {Function} onProgress - 진행률 콜백 (0-100)
 * @returns {Promise<Object>} 업로드 결과
 */
export async function uploadViaWebSocket(blob, fileName, onProgress = () => {}) {
  const ws = getWebSocket();
  if (!ws || ws.readyState !== WebSocket.OPEN) {
    throw new Error('WebSocket not connected');
  }

  const buffer = await blob.arrayBuffer();
  const totalChunks = Math.ceil(buffer.byteLength / WS_CHUNK_SIZE);

  // file_start (JSON)
  ws.send(JSON.stringify({
    type: 'file_start',
    data: { fileName, size: buffer.byteLength, totalChunks, mimeType: 'application/pdf' }
  }));

  // chunks (binary)
  for (let i = 0; i < totalChunks; i++) {
    const chunk = buffer.slice(i * WS_CHUNK_SIZE, (i + 1) * WS_CHUNK_SIZE);
    ws.send(chunk);
    onProgress(10 + ((i + 1) / totalChunks) * 80);
  }

  // file_end (JSON)
  ws.send(JSON.stringify({
    type: 'file_end',
    data: { fileName, uploadedAt: new Date().toISOString() }
  }));

  onProgress(100);
  return { success: true, fileName, pcOnline: true };
}

/**
 * 네트워크 연결 확인
 */
export function isOnline() {
  return navigator.onLine;
}

/**
 * sleep 유틸리티
 */
function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * 세션 유효성 검증
 * 업로드 전에 호출하여 세션이 아직 유효한지 확인
 */
export async function validateSession() {
  const pairingInfo = getPairingInfo();

  if (!pairingInfo) {
    return { valid: false, error: '페어링 정보 없음', requiresRepairing: true };
  }

  try {
    // poll 엔드포인트로 세션 유효성 확인
    const traceHeaders = getSentryTraceHeaders();
    const response = await fetch(
      `${pairingInfo.endpoint}/sessions/${pairingInfo.device_id}/poll`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${pairingInfo.credentials.apiKey}`,
          ...traceHeaders
        }
      }
    );

    if (response.ok) {
      return { valid: true };
    }

    if (response.status === 401) {
      return { valid: false, error: '세션이 유효하지 않습니다.', requiresRepairing: true };
    }
    if (response.status === 404) {
      return { valid: false, error: '세션을 찾을 수 없습니다.', requiresRepairing: true };
    }
    if (response.status === 410) {
      return { valid: false, error: '세션이 만료되었습니다.', requiresRepairing: true };
    }

    return { valid: false, error: `HTTP ${response.status}` };
  } catch (e) {
    return { valid: false, error: e.message };
  }
}

/**
 * R2 연결 테스트
 */
export async function testConnection() {
  const pairingInfo = getPairingInfo();

  if (!pairingInfo) {
    return { success: false, error: '페어링 정보 없음' };
  }

  try {
    const traceHeaders = getSentryTraceHeaders();
    const response = await fetch(`${pairingInfo.endpoint}/health`, {
      method: 'GET',
      headers: {
        'X-Device-ID': pairingInfo.device_id,
        ...traceHeaders
      }
    });

    if (response.ok) {
      return { success: true };
    }
    return { success: false, error: `HTTP ${response.status}` };
  } catch (e) {
    return { success: false, error: e.message };
  }
}
